import React, { useState } from "react";
import { Trash2, Pencil, Wallet } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IDR, shortDate } from "@/lib/format";
import { inputClass, btnPrimary, btnGhost } from "@/components/Brand";
import { DialogShell } from "@/components/Dialog";

/** Edits both percentages a marketer has: the client discount and their own commission. */
export const MarketerDialog = ({ marketer, onConfirm, onClose }) => {
  const { t } = useI18n();
  const [discount, setDiscount] = useState(String(marketer.discount_percent));
  const [commission, setCommission] = useState(String(marketer.commission_percent ?? 10));

  return (
    <DialogShell onClose={onClose} testId="affiliate-edit-dialog">
      <p className="eyebrow text-[#D4AF37]">{marketer.name} · {marketer.code}</p>
      <label className="block eyebrow mt-6 mb-1">{t("discount_percent")}</label>
      <input data-testid="affiliate-edit-discount" type="number" min="0" max="100" autoFocus
        className={inputClass} value={discount} onChange={(e) => setDiscount(e.target.value)} />
      <label className="block eyebrow mt-5 mb-1">{t("commission_percent")}</label>
      <input data-testid="affiliate-edit-commission" type="number" min="0" max="100"
        className={inputClass} value={commission} onChange={(e) => setCommission(e.target.value)} />
      <div className="mt-7 flex flex-wrap gap-3">
        <button data-testid="affiliate-edit-save" className={btnPrimary}
          onClick={() => onConfirm({ discount_percent: discount, commission_percent: commission })}>
          {t("save")}
        </button>
        <button data-testid="affiliate-edit-cancel" onClick={onClose} className={btnGhost}>{t("cancel")}</button>
      </div>
    </DialogShell>
  );
};

const PayoutHistory = ({ row, onDeletePayout }) => {
  const { t } = useI18n();

  return (
    <details className="mt-2" data-testid={`payout-history-${row.code}`}>
      <summary className="font-mono-accent text-[10px] tracking-[0.16em] uppercase text-[#5A5A5A] cursor-pointer hover:text-[#D4AF37]">
        {t("payout_history")} ({row.payouts.length})
      </summary>
      <ul className="mt-2 space-y-1">
        {row.payouts.map((p) => (
          <li key={p.payout_id} className="flex items-center gap-3 font-mono-accent text-[11px] text-[#A8A8A8]">
            <span>{shortDate(p.paid_at)}</span>
            <span className="text-[#F3F3F1]">{IDR(p.amount)}</span>
            {p.note && <span className="text-[#5A5A5A] truncate">{p.note}</span>}
            <button data-testid={`delete-payout-${p.payout_id}`} onClick={() => onDeletePayout(row, p)}
              aria-label={t("delete_payout")} className="text-[#5A5A5A] hover:text-[#8B0000]">
              <Trash2 size={11} />
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
};

const MarketerSummary = ({ row }) => {
  const { t } = useI18n();

  return (
    <>
      <p className="text-sm">
        {row.name}
        <span className="ml-3 font-mono-accent text-[10px] tracking-[0.2em] text-[#D4AF37]">{row.code}</span>
        <span className="ml-3 text-[11px] text-[#707070]">-{row.discount_percent}%</span>
        <span data-testid={`affiliate-status-${row.code}`}
          className={`ml-3 text-[10px] font-mono-accent tracking-[0.15em] uppercase ${
            row.active ? "text-[#4E7F55]" : "text-[#8B0000]"
          }`}>
          {row.active ? t("marketer_active") : t("marketer_inactive")}
        </span>
      </p>
      <p className="mt-1 text-[11px] text-[#707070] font-mono-accent">
        {t("revenue")}: {IDR(row.revenue)} · {t("conversions")}: {row.conversions}
        {Object.entries(row.monthly || {}).slice(0, 3).map(([m, v]) => ` · ${m} ${IDR(v)}`).join("")}
      </p>
      <p className="mt-1 text-[11px] font-mono-accent" data-testid={`affiliate-payout-summary-${row.code}`}>
        <span className="text-[#707070]">{t("commission_label")} {row.commission_percent}% · {t("earned")}: </span>
        <span className="text-[#F3F3F1]">{IDR(row.earned)}</span>
        <span className="text-[#707070]"> · {t("paid_out")}: </span>
        <span className="text-[#F3F3F1]">{IDR(row.paid_out)}</span>
        <span className="text-[#707070]"> · {t("outstanding")}: </span>
        <span className={row.outstanding > 0 ? "text-[#D4AF37]" : "text-[#4E7F55]"}>{IDR(row.outstanding)}</span>
      </p>
    </>
  );
};

/** One marketer: performance, payout history and the row actions. */
export const MarketerRow = ({ row, onPay, onToggle, onEdit, onDelete, onDeletePayout }) => {
  const { t } = useI18n();

  return (
    <div data-testid={`affiliate-row-${row.code}`}
      className="flex flex-wrap items-center justify-between gap-4 border-b border-[#1E1E1E] pb-3">
      <div className="min-w-0">
        <MarketerSummary row={row} />
        {(row.payouts || []).length > 0 && <PayoutHistory row={row} onDeletePayout={onDeletePayout} />}
      </div>
      <span className="flex items-center gap-4">
        <button data-testid={`affiliate-payout-${row.code}`} onClick={() => onPay(row)}
          disabled={row.outstanding <= 0}
          className="text-[10px] font-mono-accent tracking-[0.15em] uppercase border border-[#2A2A2A] px-3 py-1.5 text-[#A8A8A8] enabled:hover:border-[#D4AF37] enabled:hover:text-[#D4AF37] disabled:text-[#3d3d3d] transition-colors">
          <Wallet size={12} className="inline mr-2 -mt-0.5" />{t("mark_paid")}
        </button>
        <button data-testid={`affiliate-toggle-${row.code}`} onClick={() => onToggle(row)}
          className={`text-[10px] font-mono-accent tracking-[0.15em] uppercase border px-3 py-1.5 transition-colors ${
            row.active
              ? "border-[#2A2A2A] text-[#A8A8A8] hover:border-[#8B0000] hover:text-[#8B0000]"
              : "border-[#2A2A2A] text-[#A8A8A8] hover:border-[#D4AF37] hover:text-[#D4AF37]"
          }`}>
          {row.active ? t("deactivate") : t("activate")}
        </button>
        <button data-testid={`affiliate-edit-${row.code}`} onClick={() => onEdit(row)}
          className="text-[#5A5A5A] hover:text-[#D4AF37]" aria-label={t("edit")}><Pencil size={14} /></button>
        <button data-testid={`affiliate-delete-${row.code}`} onClick={() => onDelete(row)}
          className="text-[#5A5A5A] hover:text-[#8B0000]" aria-label={t("remove")}><Trash2 size={14} /></button>
      </span>
    </div>
  );
};
